import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ShoppingBag, Truck, Package, Phone, MapPin, Plus, X as IconX, ArrowRight, Trash2, Search, RotateCcw, PackageCheck, Wallet, ClipboardList } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  catalogProductsOf, ONLINE_ORDER_STAGES, ONLINE_ORDER_STAGE_META,
  onlineOrdersOf, addOnlineOrder, updateOnlineOrder, deleteOnlineOrder,
  advanceOnlineOrder, nextOrderStage,
} from '../../data/store';
import { CustomSelect } from '../../components/CustomSelect';
import { SideDrawer } from '../../components/SideDrawer';

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

// ---- Ne order modal ----
const NewOrderModal = ({ org, accent, onClose, onCreate }) => {
  const [customer, setCustomer] = useState('');
  const [phone, setPhone] = useState('');
  const [fulfilment, setFulfilment] = useState('delivery');
  const [address, setAddress] = useState('');
  const [method, setMethod] = useState('Card');
  const [note, setNote] = useState('');
  const [fee, setFee] = useState('2.00');
  const [lines, setLines] = useState([{ name: '', label: '', qty: '1', price: '' }]);

  const productNames = useMemo(() => catalogProductsOf(org.id).map((p) => p.name), [org.id]);
  const isDelivery = fulfilment === 'delivery';

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { name: '', label: '', qty: '1', price: '' }]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const validLines = lines
    .map((l) => ({ name: l.name.trim(), label: l.label.trim(), qty: Number(l.qty) || 0, price: Number(l.price) || 0 }))
    .filter((l) => l.name && l.qty > 0);
  const subtotal = validLines.reduce((s, l) => s + l.qty * l.price, 0);
  const feeNum = isDelivery ? (Number(fee) || 0) : 0;
  const total = subtotal + feeNum;
  const canSubmit = customer.trim() && validLines.length > 0 && (!isDelivery || address.trim());

  const submit = () => {
    if (!canSubmit) return;
    onCreate({
      orgId: org.id,
      customer: customer.trim(),
      phone: phone.trim(),
      address: isDelivery ? address.trim() : '',
      lines: validLines,
      subtotal: +subtotal.toFixed(2),
      fee: +feeNum.toFixed(2),
      total: +total.toFixed(2),
      fulfilment,
      method,
      note: note.trim(),
      status: 'placed',
      placedAt: today(),
    });
  };

  return (
    <SideDrawer onClose={onClose}>
      <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }} aria-hidden="true">
            <ShoppingBag size={18} />
          </div>
          <div>
            <h3 className="text-base font-extrabold text-[#0d0d0d]">New online order</h3>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Record a storefront order</p>
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
          <IconX size={16} />
        </button>
      </header>

      <div className="px-5 py-5 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Customer name</span>
            <input type="text" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="e.g. Olivia Frank"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Phone</span>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
        </div>

        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Fulfilment</span>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {[
              { id: 'delivery', label: 'Delivery', icon: Truck },
              { id: 'pickup', label: 'Store pickup', icon: ShoppingBag },
            ].map((f) => {
              const active = fulfilment === f.id;
              return (
                <button key={f.id} type="button" onClick={() => setFulfilment(f.id)} aria-pressed={active}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 border text-[11px] font-bold transition-all ${active ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] text-[var(--theme-primary)]' : 'border-[var(--theme-tertiary)] text-slate-500'
                    }`}>
                  <f.icon size={13} aria-hidden="true" /> {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {isDelivery && (
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Delivery address</span>
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, city"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Items</span>
            <button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--theme-primary)]">
              <Plus size={12} /> Add item
            </button>
          </div>
          <datalist id="online-order-product-names">
            {productNames.map((n) => <option key={n} value={n} />)}
          </datalist>
          <div className="flex flex-col gap-2">
            {lines.map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="text" list="online-order-product-names" value={line.name} onChange={(e) => setLine(i, { name: e.target.value })} placeholder="Item name"
                  className="flex-1 min-w-0 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
                <input type="text" value={line.label} onChange={(e) => setLine(i, { label: e.target.value })} placeholder="Variant"
                  className="w-24 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d]" />
                <input type="number" min="1" value={line.qty} onChange={(e) => setLine(i, { qty: e.target.value })} placeholder="Qty" aria-label="Quantity"
                  className="w-14 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d] text-center" />
                <input type="number" min="0" step="0.01" value={line.price} onChange={(e) => setLine(i, { price: e.target.value })} placeholder="Price" aria-label="Unit price"
                  className="w-20 rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-2 text-xs font-medium text-[#0d0d0d] text-right" />
                <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} aria-label="Remove item"
                  className="p-1.5 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 disabled:opacity-30">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Delivery fee (₹)</span>
            <input type="number" min="0" step="0.01" value={fee} disabled={!isDelivery} onChange={(e) => setFee(e.target.value)}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] disabled:opacity-50" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Payment</span>
            <CustomSelect
              value={method}
              onChange={setMethod}
              options={[
                { value: 'Card', label: 'Card' },
                { value: 'Cash', label: 'Cash' }
              ]}
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Note (optional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Ring the bell"
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
        </div>
      </div>

      <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-between gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        <div className="text-[11px] font-bold text-slate-500">
          Subtotal {money(subtotal)} · Fee {money(feeNum)} · <span className="text-[#0d0d0d]">Total {money(total)}</span>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-50"
            style={{ background: accent }}>
            Create order
          </button>
        </div>
      </footer>
    </SideDrawer>
  );
};

NewOrderModal.propTypes = {
  org: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

// ---- Order card ----
const OrderCard = ({ order, accent, onAdvance, onCancel, onReopen, onDelete }) => {
  const meta = ONLINE_ORDER_STAGE_META[order.status];
  const next = nextOrderStage(order.status);
  const cancelled = order.status === 'cancelled';
  const isDelivery = order.fulfilment === 'delivery';
  const units = order.lines.reduce((s, l) => s + l.qty, 0);

  return (
    <motion.article layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border rounded-lg p-4 flex flex-col gap-3 flex-1"
      style={{ borderColor: 'var(--theme-tertiary)', borderLeft: `3px solid ${meta.color}` }}>      <header className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{order.customer}</p>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 whitespace-nowrap truncate min-w-0">{order.id} · {order.placedAt}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] px-2 py-0.5 rounded-lg shrink-0"
            style={{ background: `${meta.color}14`, color: meta.color }}>
            {meta.label}
          </span>
          {!cancelled && (
            <button type="button" onClick={() => onCancel(order)} aria-label="Cancel order"
              className="p-1 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors shrink-0">
              <IconX size={13} />
            </button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-2 text-[11px] font-semibold">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
          {isDelivery ? <Truck size={11} aria-hidden="true" /> : <ShoppingBag size={11} aria-hidden="true" />}
          {isDelivery ? 'Delivery' : 'Pickup'}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-50 text-slate-500">{order.method}</span>
      </div>

      <dl className="flex flex-col gap-1 text-[11px] text-slate-600">
        {order.phone && (
          <div className="inline-flex items-center gap-1.5"><Phone size={11} aria-hidden="true" /> <span className="truncate">{order.phone}</span></div>
        )}
        {isDelivery && order.address && (
          <div className="inline-flex items-center gap-1.5"><MapPin size={11} aria-hidden="true" /> <span className="truncate">{order.address}</span></div>
        )}
      </dl>

      <ul className="flex flex-col gap-1 text-[11px] text-slate-600">
        {order.lines.map((l, i) => (
          <li key={i} className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <Package size={11} className="shrink-0 text-slate-400" aria-hidden="true" />
              <span className="truncate">{l.name}{l.label ? ` · ${l.label}` : ''}</span>
            </span>
            <span className="font-bold text-[#0d0d0d] shrink-0">×{l.qty}</span>
          </li>
        ))}
      </ul>

      <div className="rounded-lg bg-[var(--theme-secondary)] px-3 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{units} unit{units === 1 ? '' : 's'}</span>
        <span className="text-[12px] font-extrabold text-[#0d0d0d]">{money(order.total)}</span>
      </div>

      {order.note && <p className="text-[11px] font-medium text-slate-400 italic truncate">“{order.note}”</p>}

      <footer className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-[var(--theme-tertiary)] flex-wrap w-full">
        {cancelled ? (
          <>
            <button type="button" onClick={() => onReopen(order)}
              className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full border border-[var(--theme-tertiary)] text-slate-600 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary-tint)] text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap shrink-0">
              <RotateCcw size={12} /> Reopen
            </button>
            <button type="button" onClick={() => onDelete(order)} aria-label="Delete order"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors shrink-0 ml-auto">
              <Trash2 size={13} />
            </button>
          </>
        ) : (
          <>
            {next && (
              <button type="button" onClick={() => onAdvance(order)}
                className="inline-flex items-center justify-center gap-1.5 h-8 px-3.5 rounded-full text-white text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap shrink-0 border border-transparent ml-auto"
                style={{ background: accent }}>
                {ONLINE_ORDER_STAGE_META[next].label} <ArrowRight size={12} />
              </button>
            )}
          </>
        )}
      </footer>
    </motion.article>
  );
};

OrderCard.propTypes = {
  order: PropTypes.object.isRequired,
  accent: PropTypes.string.isRequired,
  onAdvance: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <article className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-4 py-3 flex items-center gap-3">
    <span className="flex items-center justify-center shrink-0" style={{ color }} aria-hidden="true">
      <Icon size={24} />
    </span>
    <div>
      <p className="text-lg font-extrabold text-[#0d0d0d] leading-none">{value}</p>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mt-1">{label}</p>
    </div>
  </article>
);

Stat.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
};

// ---- Online orders panel ----
export const OnlineOrdersPanel = ({ org, showNotification }) => {
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const accent = org.theme.primary;

  const list = useMemo(() => {
    let rows = onlineOrdersOf(org.id);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((o) => `${o.customer} ${o.id} ${o.phone} ${o.address}`.toLowerCase().includes(q));
    }
    return rows;
  }, [org.id, search, version]);

  const byStage = useMemo(() => {
    const map = {};
    ONLINE_ORDER_STAGES.forEach((s) => { map[s] = []; });
    map.cancelled = [];
    list.forEach((o) => { (map[o.status] || map.placed).push(o); });
    return map;
  }, [list]);

  const revenue = useMemo(
    () => byStage.completed.reduce((s, o) => s + o.total, 0),
    [byStage],
  );

  const advance = (o) => {
    advanceOnlineOrder(o.id);
    const next = nextOrderStage(o.status);
    if (showNotification && next) showNotification(`${o.customer} → ${ONLINE_ORDER_STAGE_META[next].label}`, 'success');
    bump();
  };
  const cancel = (o) => {
    updateOnlineOrder(o.id, { status: 'cancelled' });
    if (showNotification) showNotification(`Order ${o.id} cancelled.`);
    bump();
  };
  const reopen = (o) => {
    updateOnlineOrder(o.id, { status: 'placed' });
    if (showNotification) showNotification(`Order ${o.id} reopened.`, 'success');
    bump();
  };
  const removeRow = (o) => {
    deleteOnlineOrder(o.id);
    if (showNotification) showNotification('Order removed.', 'success');
    bump();
  };
  const createOrder = (record) => {
    addOnlineOrder(record);
    if (showNotification) showNotification(`Order for ${record.customer} created.`, 'success');
    setNewOpen(false);
    bump();
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders…"
            className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none" />
        </div>
        <button type="button" onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg transition-opacity hover:opacity-90"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
          <Plus size={14} aria-hidden="true" /> New order
        </button>
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Stat icon={ClipboardList} label="In pipeline" value={ONLINE_ORDER_STAGES.reduce((s, st) => s + byStage[st].length, 0)} color="#0369a1" />
        <Stat icon={ShoppingBag} label="New orders" value={byStage.placed.length} color={ONLINE_ORDER_STAGE_META.placed.color} />
        <Stat icon={PackageCheck} label="Completed" value={byStage.completed.length} color={ONLINE_ORDER_STAGE_META.completed.color} />
        <Stat icon={Wallet} label="Order revenue" value={money(revenue)} color="#0a7d3c" />
      </section>

      <section className="flex flex-col md:flex-row gap-4 overflow-x-auto pb-4 w-full min-w-0 custom-scrollbar">
        {ONLINE_ORDER_STAGES.map((stage) => {
          const meta = ONLINE_ORDER_STAGE_META[stage];
          const cards = byStage[stage];
          return (
            <div key={stage} className="flex flex-col gap-3 flex-1 min-w-[270px]">
              <header className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} aria-hidden="true" />
                  <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">{meta.label}</h3>
                </div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-lg" style={{ background: `${meta.color}14`, color: meta.color }}>
                  {cards.length}
                </span>
              </header>
              <div className="flex flex-col gap-3 min-h-[80px] rounded-lg border border-dashed border-[var(--theme-tertiary)] p-2.5 flex-1">
                <AnimatePresence>
                  {cards.map((o) => (
                    <OrderCard key={o.id} order={o} accent={accent} onAdvance={advance} onCancel={cancel} onReopen={reopen} onDelete={removeRow} />
                  ))}
                </AnimatePresence>
                {cards.length === 0 && (
                  <p className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">{meta.hint}</p>
                )}
              </div>
            </div>
          );
        })}
      </section>

      {byStage.cancelled.length > 0 && (
        <section className="flex flex-col gap-3">
          <header className="flex items-center gap-2 px-1">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: ONLINE_ORDER_STAGE_META.cancelled.color }} aria-hidden="true" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-[#0d0d0d]">Cancelled</h3>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-lg" style={{ background: `${ONLINE_ORDER_STAGE_META.cancelled.color}14`, color: ONLINE_ORDER_STAGE_META.cancelled.color }}>
              {byStage.cancelled.length}
            </span>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <AnimatePresence>
              {byStage.cancelled.map((o) => (
                <OrderCard key={o.id} order={o} accent={accent} onAdvance={advance} onCancel={cancel} onReopen={reopen} onDelete={removeRow} />
              ))}
            </AnimatePresence>
          </div>
        </section>
      )}

      {list.length === 0 && (
        <div className="py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
          <ShoppingBag size={28} className="mx-auto text-slate-300" aria-hidden="true" />
          <p className="mt-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No online orders yet.</p>
          <button type="button" onClick={() => setNewOpen(true)} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
            <Plus size={12} /> Record an order
          </button>
        </div>
      )}

      {newOpen && (
        <NewOrderModal org={org} accent={accent} onClose={() => setNewOpen(false)} onCreate={createOrder} />
      )}
    </div>
  );
};

OnlineOrdersPanel.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
